import { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button } from '../../../components/Button/Button';

type Props = {
	value: string[]
	onChange: (value: string[]) => void
	disabled?: boolean
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB per file

const readAsDataUrl = (file: File): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(file);
	});
};

export const GalleryEditor = ({ value, onChange, disabled }: Props) => {
	const inputRef = useRef<HTMLInputElement | null>(null);

	const openPicker = useCallback(() => {
		inputRef.current?.click();
	}, []);

	const onFilesSelected = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files || []);
			if (files.length === 0) return;

			const accepted: string[] = [];
			for (const file of files) {
				if (!ACCEPTED_TYPES.includes(file.type)) {
					toast.error(`Tip fajla nije podržan: ${file.name}`);
					continue;
				}
				if (file.size > MAX_FILE_SIZE_BYTES) {
					toast.error(`Fajl je prevelik (maks. 5 MB): ${file.name}`);
					continue;
				}
				try {
					accepted.push(await readAsDataUrl(file));
				} catch {
					toast.error(`Greška pri učitavanju fajla: ${file.name}`);
				}
			}

			if (accepted.length > 0) {
				onChange([...value, ...accepted]);
			}

			// Reset so the same file can be re-selected later.
			if (inputRef.current) {
				inputRef.current.value = '';
			}
		},
		[value, onChange]
	);

	const removeCallback = useCallback(
		(index: number) => {
			onChange(value.filter((_, i) => i !== index));
		},
		[value, onChange]
	);

	const moveCallback = useCallback(
		(index: number, direction: -1 | 1) => {
			const target = index + direction;
			if (target < 0 || target >= value.length) return;
			const next = [...value];
			[next[index], next[target]] = [next[target], next[index]];
			onChange(next);
		},
		[value, onChange]
	);

	return (
		<div>
			<div className='label-input'>
				<label>Galerija</label>
			</div>

			{!disabled && (
				<div className='flex-start-container' style={{ alignItems: 'center', gap: 8, marginBottom: 8 }}>
					<input
						ref={inputRef}
						type='file'
						accept={ACCEPTED_TYPES.join(',')}
						multiple
						onChange={onFilesSelected}
						style={{ display: 'none' }}
					/>
					<Button type='button' value='Dodaj slike' onClick={openPicker} />
					<span style={{ color: '#888' }}>JPG, PNG, WEBP ili GIF, maks. 5 MB po slici.</span>
				</div>
			)}

			{value.length === 0 && (
				<div style={{ color: '#888', fontStyle: 'italic', marginBottom: 8 }}>
					Nema slika u galeriji.
				</div>
			)}

			{value.map((entry, index) => (
				<div
					key={`${index}-${entry.slice(0, 32)}`}
					className='flex-start-container'
					style={{ alignItems: 'center', gap: 8, marginBottom: 6 }}
				>
					<img
						src={entry}
						alt=''
						style={{ width: 80, height: 60, objectFit: 'cover', border: '1px solid #ddd' }}
					/>
					<div style={{ flex: 1, color: '#666', fontSize: 12, wordBreak: 'break-all' }}>
						{entry.startsWith('data:') ? '(nova slika, biće sačuvana pri snimanju)' : entry}
					</div>
					{!disabled && (
						<>
							<Button
								type='button'
								value='↑'
								onClick={() => moveCallback(index, -1)}
								disabled={index === 0}
								outline
							/>
							<Button
								type='button'
								value='↓'
								onClick={() => moveCallback(index, 1)}
								disabled={index === value.length - 1}
								outline
							/>
							<Button
								type='button'
								value='Ukloni'
								onClick={() => removeCallback(index)}
								outline
							/>
						</>
					)}
				</div>
			))}
		</div>
	);
};
